import React from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'

import { Dashboard } from '@components/common/Dashboard/Dashboard'
import { NavTabs } from '@components/common/NavTabs'
import { PageLayout } from '@components/common/PageLayout'
import { SearchBar } from '@components/common/SearchBar'
import {
  PermissionGuardedRoute,
  useDashboardPermissions,
} from '@components/utils/DashboardPermissions'
import { ButtonGroup, Loading, ToolbarButton, useMediaQueryContext } from '@fourthwall/components'
import { DownloadIcon } from '@fourthwall/icons'
import { lazyWithRetry } from '@fourthwall/utils/dist/lazyWithRetry'
import { useWebViewBridge } from '@fourthwall/webview-bridge'
import { useSearchQuery } from '@modules/Dashboard/hooks/useSearchQuery'
import {
  MiniRecommendations,
  MiniRecommendationsSkeleton,
  useMiniRecommendationsData,
} from '@modules/Dashboard/modules/MiniRecommendations'
import { routing } from '@utils/routing'

import { useOrdersCsvExport } from './modules/CsvExport'
import { DonationsList } from './pages/DonationsList'
import { useGiveawayContributionsQuery } from './pages/GiveawaysList'
import { OrdersFilter, OrdersList } from './pages/OrdersList'

const GiveawaysContainer = lazyWithRetry(() => import('./pages/GiveawaysList'))

export const Orders = () => {
  const { hasPermission } = useDashboardPermissions()
  const media = useMediaQueryContext()
  const { resetSearchValue, searchValue, setSearchValue, searchOptions } = useSearchQuery()
  const { ordersList } = useOrdersCsvExport()
  const { pathname } = useLocation()
  const { giveawayContributionsQuery } = useGiveawayContributionsQuery()
  const { isLoading, data: miniRecommendationsData } = useMiniRecommendationsData()
  const { isWebView } = useWebViewBridge()

  const isOrdersTab = pathname === routing.contributions.orders.all

  const renderSearchBar = () => {
    if (!isOrdersTab) return null

    return (
      <SearchBar
        name="search"
        placeholder="Search orders"
        value={searchValue}
        maxLength={searchOptions.maxLength}
        onValueChange={(value: string) => setSearchValue(value)}
        onActionIconClick={resetSearchValue}
      />
    )
  }

  const getToolbarContent = () => (
    <ButtonGroup space="medium">
      {!isWebView && (
        <Loading isLoading={ordersList.isLoading}>
          <ToolbarButton label="Export orders" icon={DownloadIcon} onClick={ordersList.download} />
        </Loading>
      )}
      {isOrdersTab && <OrdersFilter />}
      {media.tablet && renderSearchBar()}
    </ButtonGroup>
  )

  const tabs = [
    {
      label: 'Orders',
      to: routing.contributions.orders.all,
      element: <OrdersList />,
    },
    {
      label: 'Donations',
      to: routing.contributions.orders.donations.all,
      hidden: !hasPermission('contributions.donations'),
      element: (
        <PermissionGuardedRoute permission="contributions.donations" element={<DonationsList />} />
      ),
    },
    {
      label: 'Twitch gifts',
      to: routing.contributions.orders.giveaways.all,
      element: <GiveawaysContainer />,
      hidden: !giveawayContributionsQuery?.data?.result.length,
    },
  ]

  // This is a temporary solution that should be fixed once whole dashboard router is migrated to react-router 6.
  const getElement = (pathname: string): JSX.Element => {
    return tabs[tabs.findIndex(({ to }) => to === pathname)]?.element || React.Fragment
  }

  return (
    <PageLayout size="large">
      <Dashboard.Section
        name="Orders"
        bottomContent={
          <>
            {!media.tablet && renderSearchBar()}

            {!isWebView && isLoading && <MiniRecommendationsSkeleton />}

            {!isWebView && !isLoading && (
              <MiniRecommendations data={miniRecommendationsData} page="orders" />
            )}
          </>
        }
        bottomMargin="medium"
        toolbarContent={getToolbarContent()}
      >
        <Dashboard.Section.Item>
          <NavTabs tabs={tabs.filter((tab) => !tab.hidden)} />
        </Dashboard.Section.Item>
        <Routes>
          <Route index element={getElement(pathname)} />
          {/* <Route path={routing.contributions.orders.all} element={<Orders />} /> */}
          {/* <Route
            path={routing.contributions.orders.donations.all}
            element={<DonationsContainer />}
          /> */}
          {/* <Route
            path={routing.contributions.orders.giveaways.all}
            element={<GiveawaysContainer />}
          /> */}
        </Routes>
      </Dashboard.Section>
    </PageLayout>
  )
}
